import React, { useEffect, useState } from "react";
import { ExternalLink, Maximize2, Navigation, X } from "lucide-react";
import { GOOGLE_MAPS_KEY, HAS_KEY } from "./utils";

/**
 * Detects if Google Street View has imagery for the given address. Uses the
 * public Street View metadata API (no quota usage for metadata checks).
 * Returns "OK" / "ZERO_RESULTS" / "UNKNOWN". We fail open (UNKNOWN → show).
 */
async function checkStreetViewAvailability(address) {
  if (!HAS_KEY || !address) return "UNKNOWN";
  try {
    const u = `https://maps.googleapis.com/maps/api/streetview/metadata?location=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_KEY}`;
    const r = await fetch(u);
    if (!r.ok) return "UNKNOWN";
    const j = await r.json();
    return j.status || "UNKNOWN";
  } catch (_e) {
    return "UNKNOWN";
  }
}

export function MapHero({ address, ownerLabel, imageUrl, imageLoading = false, compact = false, className = "" }) {
  const [hasStreetView, setHasStreetView] = useState(null); // null = checking, true/false = known
  const [expanded, setExpanded] = useState(null);
  const [selectedView, setSelectedView] = useState("image");

  useEffect(() => {
    let cancelled = false;
    if (!address) { setHasStreetView(null); return; }
    // Only pre-flight if we actually want to render a streetview image (no saved imageUrl yet).
    if (imageUrl) { setHasStreetView(true); return; }
    checkStreetViewAvailability(address).then((status) => {
      if (cancelled) return;
      // Hide the image only when Google explicitly says ZERO_RESULTS or NOT_FOUND.
      if (status === "ZERO_RESULTS" || status === "NOT_FOUND") setHasStreetView(false);
      else setHasStreetView(true);
    });
    return () => { cancelled = true; };
  }, [address, imageUrl]);

  const encoded = encodeURIComponent(address || "");
  // Only build the streetview URL when metadata confirmed imagery exists.
  const canShowStreetview = HAS_KEY && hasStreetView === true;
  const streetViewImg = canShowStreetview
    ? `https://maps.googleapis.com/maps/api/streetview?size=1600x400&location=${encoded}&fov=80&pitch=0&key=${GOOGLE_MAPS_KEY}`
    : null;
  const embedSrc = HAS_KEY
    ? `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_KEY}&q=${encoded}&zoom=16`
    : `https://www.google.com/maps?q=${encoded}&z=16&output=embed`;
  const openUrl = `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
  const heroImage = streetViewImg || imageUrl || null;
  const heroImageTestId = streetViewImg ? "lead-streetview-image" : "lead-saved-map-image";
  const heroImageAlt = streetViewImg ? `Property at ${address}` : `Saved map for ${address}`;
  const activeView = heroImage ? selectedView : "map";

  useEffect(() => {
    setSelectedView(heroImage ? "image" : "map");
  }, [heroImage]);

  if (!address) return null;

  return (
    <>
      <div className={`rounded-md overflow-hidden border border-zinc-200 bg-white shadow-sm ${compact ? "flex flex-col" : "mb-3"} ${className}`} data-testid="lead-map-section">
        <div className="min-h-11 px-3 py-2 border-b border-zinc-200 flex flex-wrap items-center justify-between gap-2">
          {heroImage && (
            <div className="inline-flex rounded-md border border-zinc-200 bg-zinc-50 p-0.5">
              <button
                type="button"
                onClick={() => setSelectedView("image")}
                className={`h-7 px-3 rounded text-xs font-bold transition-colors ${activeView === "image" ? "bg-white text-zinc-950 shadow-sm" : "text-zinc-500 hover:text-zinc-950"}`}
              >
                Image
              </button>
              <button
                type="button"
                onClick={() => setSelectedView("map")}
                className={`h-7 px-3 rounded text-xs font-bold transition-colors ${activeView === "map" ? "bg-white text-zinc-950 shadow-sm" : "text-zinc-500 hover:text-zinc-950"}`}
              >
                Map
              </button>
            </div>
          )}
          {!heroImage && <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Map</p>}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setExpanded(activeView)}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 px-2.5 py-1.5 text-xs font-bold text-zinc-950"
              data-testid={activeView === "image" ? "image-expand" : "map-expand"}
            >
              <Maximize2 className="h-3.5 w-3.5" /> Expand
            </button>
            {activeView === "map" && (
              <>
                <a
                  href={openUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 px-2.5 py-1.5 text-xs font-bold text-zinc-950"
                  data-testid="map-open"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Open
                </a>
                <a
                  href={directionsUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-zinc-950 hover:bg-zinc-800 px-2.5 py-1.5 text-xs font-bold text-white"
                  data-testid="map-directions"
                >
                  <Navigation className="h-3.5 w-3.5" /> Directions
                </a>
              </>
            )}
          </div>
        </div>

        <div className={`relative ${compact ? "flex-1 min-h-[420px]" : ""}`}>
          {activeView === "image" && heroImage ? (
            <>
              <img
                src={heroImage}
                alt={heroImageAlt}
                data-testid={heroImageTestId}
                className={`w-full object-cover ${compact ? "h-full min-h-[420px]" : "h-64"}`}
              />
              {ownerLabel && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-5">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 mb-1">Property of</p>
                  <p className="text-xl font-black tracking-tight text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {ownerLabel}
                  </p>
                </div>
              )}
            </>
          ) : (
            <iframe
              title={`Map of ${address}`}
              data-testid="lead-map-iframe"
              width="100%"
              height={compact ? "420" : "380"}
              className={compact ? "h-full min-h-[420px]" : ""}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={embedSrc}
              allowFullScreen
              style={{ border: 0, display: "block" }}
            />
          )}
          {imageLoading && (
            <div />
          )}
          </div>
      </div>

      {expanded && (
        <div className="fixed inset-0 z-50 bg-black/75 p-4 sm:p-6 lg:p-8" role="dialog" aria-modal="true">
          <div className="h-full rounded-md bg-white shadow-2xl overflow-hidden flex flex-col">
            <div className="h-12 px-4 border-b border-zinc-200 flex items-center justify-between gap-3">
              <p className="text-sm font-bold text-zinc-950 truncate">
                {expanded === "image" ? "Property image" : "Property map"}
              </p>
              <button
                type="button"
                onClick={() => setExpanded(null)}
                className="h-8 w-8 rounded-md inline-flex items-center justify-center text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {expanded === "image" && heroImage ? (
              <div className="flex-1 min-h-0 bg-zinc-100 flex items-center justify-center">
                <img src={heroImage} alt={heroImageAlt} className="max-h-full max-w-full object-contain" />
              </div>
            ) : (
              <iframe
                title={`Expanded map of ${address}`}
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={embedSrc}
                allowFullScreen
                className="flex-1"
                style={{ border: 0, display: "block" }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

export { checkStreetViewAvailability };
